import { ref } from 'vue'
import { RequestService } from '../services/RequestService'
import { useTransformLearningStore } from '../stores/TransformLearningStore'
import { useTransformOwnerContext } from './useTransformOwnerContext'

const DEFAULT_ERROR_MESSAGE = 'Failed to load the progress for this user'
const NOT_ALLOWED_MESSAGE = 'You do not have access to the progress of this user'

/*
 * "View Progress" for another user, shared by the Transform Tracker and the Admin Center
 * certification table. It reuses the Transform dashboard's side panel
 * (BcstCourseProgressPanel), which renders whatever the TransformLearningStore holds. To point
 * it at another user we enter the admin view-as context — the same mechanism the Transform
 * module uses — and the store's own watch on ownerUid invalidates the previous target's
 * progress, so a row can never show the data of the row opened before it.
 *
 * The context stays active while the panel is open (leaving it would make the store re-fetch
 * the admin's own progress underneath the panel) and is dropped on close.
 * `openingProgressUid` blocks a second row while the first is still loading — that in-flight
 * guard is also what keeps two fetches from racing.
 *
 * The consumer binds `progressPanelRef` to the panel and `closeProgressPanel` to its `hide`.
 *
 * @param {Object} options
 * @param {string} options.source - Error-reporting prefix (e.g. 'TransformTracker').
 * @param {(message: string) => void} [options.onError] - Receives a user-facing message.
 */
export function useTransformProgressPanel({ source, onError } = {}) {
  const { handleErrors } = RequestService()
  const learningStore = useTransformLearningStore()
  const ownerContext = useTransformOwnerContext()

  const progressPanelRef = ref(null)
  const openingProgressUid = ref('')
  const progressOwnerName = ref('')

  /* Back to the admin's own context, however the panel was dismissed. */
  function closeProgressPanel() {
    ownerContext.exit()
    progressOwnerName.value = ''
  }

  function fail(message) {
    onError?.(message)
    closeProgressPanel()
  }

  /**
   * @param {{uid: string, name?: string}} target
   */
  async function viewProgress({ uid, name = '' } = {}) {
    if (!uid || openingProgressUid.value) return

    openingProgressUid.value = uid
    try {
      ownerContext.enter(uid)
      progressOwnerName.value = name
      await learningStore.loadDashboard()
      if (learningStore.error) {
        fail(learningStore.error)
        return
      }
      /*
       * The server refused the target (another agency, or the user does not exist) and the
       * store dropped the context. Opening now would show the admin's own progress under
       * the target's name. Opening one's own row never enters the context, so it is exempt.
       */
      const isSelf = uid === localStorage.getItem('userUid')
      if (!isSelf && !ownerContext.isActive.value) {
        fail(NOT_ALLOWED_MESSAGE)
        return
      }
      await progressPanelRef.value?.open()
    } catch (err) {
      handleErrors(`${source}.viewProgress`, err)
      fail(err?.message || DEFAULT_ERROR_MESSAGE)
    } finally {
      openingProgressUid.value = ''
    }
  }

  return { progressPanelRef, openingProgressUid, progressOwnerName, viewProgress, closeProgressPanel }
}
